/* Weekly Driver Hours & Payroll report UI (Nanak + Corporate + Driver).
   Replaces the former QuickBooks panels. Week nav + per-driver daily/weekly/overtime
   table + individual and consolidated PDF download. */
import { useCallback, useEffect, useState } from "react";
import { Loader2, FileDown, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { api } from "@/lib/auth";
import { Panel, CardLabel } from "@/components/portal/opsui";

const DAYS = ["Thursday", "Friday", "Saturday", "Sunday", "Monday", "Tuesday", "Wednesday"];
const Spin = () => <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-gold-light" /></div>;

async function download(url, name) {
  const r = await api.get(url, { responseType: "blob" });
  const u = URL.createObjectURL(r.data);
  const a = document.createElement("a"); a.href = u; a.download = name; document.body.appendChild(a); a.click();
  a.remove(); setTimeout(() => URL.revokeObjectURL(u), 30000);
}

function fmtWeek(iso, end) {
  const s = new Date(iso), e = new Date(end); e.setDate(e.getDate() - 1);
  const o = { month: "short", day: "numeric" };
  return `${s.toLocaleDateString("en-US", o)} – ${e.toLocaleDateString("en-US", { ...o, year: "numeric" })}`;
}

export function PayrollReports({ base, companyId }) {
  const [week, setWeek] = useState(null);         // ISO date string of selected week start
  const [summary, setSummary] = useState(null);
  const [approving, setApproving] = useState(null);
  const cq = companyId ? `company_id=${companyId}` : "";
  const join = (...p) => p.filter(Boolean).join("&");

  const load = useCallback(() => {
    setSummary(null);
    const qs = join(cq, week && `week_start=${week.slice(0, 10)}`);
    api.get(`${base}/summary${qs ? `?${qs}` : ""}`).then((r) => {
      setSummary(r.data); if (!week) setWeek(r.data.week_start);
    }).catch(() => setSummary({ rows: [] }));
  }, [base, cq, week]);
  useEffect(() => { load(); }, [load]);

  const step = (days) => { const d = new Date(week); d.setDate(d.getDate() + days); setWeek(d.toISOString()); };
  const pdfQS = (extra) => { const qs = join(cq, week && `week_start=${week.slice(0, 10)}`, extra); return qs ? `?${qs}` : ""; };

  const approve = async (driverId) => {
    setApproving(driverId);
    try {
      await api.post(`${base}/approve`, { driver_id: driverId, company_id: companyId || undefined, week_start: week ? week.slice(0, 10) : undefined });
      load();
    } finally { setApproving(null); }
  };
  const canApprove = summary?.can_approve;

  return (
    <div className="p-6" data-testid="payroll-reports-section">
      <Panel>
        <CardLabel icon={Clock} right={
          <div className="flex items-center gap-2">
            <button data-testid="payroll-prev" onClick={() => step(-7)} className="rounded-md border border-gold/30 p-1.5 text-gold-light hover:bg-gold/10"><ChevronLeft className="h-4 w-4" /></button>
            <span className="min-w-[150px] text-center text-xs text-ivory" data-testid="payroll-week-label">{summary ? fmtWeek(summary.week_start, summary.week_end) : "…"}</span>
            <button data-testid="payroll-next" onClick={() => step(7)} className="rounded-md border border-gold/30 p-1.5 text-gold-light hover:bg-gold/10"><ChevronRight className="h-4 w-4" /></button>
            <button data-testid="payroll-company-pdf" onClick={() => download(`${base}/company-report.pdf${pdfQS()}`, "payroll-company.pdf")} className="ml-2 inline-flex items-center gap-1.5 rounded-full bg-gold px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-obsidian-900"><FileDown className="h-3.5 w-3.5" /> Company PDF</button>
          </div>}>Driver Hours & Payroll</CardLabel>
        <p className="mb-3 text-[11px] text-dim-charcoal">Payroll week Thu 12:00 AM → Wed 11:59 PM ({summary?.tz || "America/New_York"}). Overtime after 40h/week. Trip-clock hours only.</p>
        {summary === null ? <Spin /> : summary.rows.length === 0 ? (
          <div className="py-12 text-center" data-testid="payroll-empty"><Clock className="mx-auto h-7 w-7 text-muted-steel" /><p className="mt-3 text-sm text-ivory">No driver hours for this week</p></div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full text-xs">
            <thead><tr className="border-b border-line text-left uppercase tracking-wider text-muted-steel">
              <th className="py-2 pr-2">Driver</th>{DAYS.map((d) => <th key={d} className="px-1 text-center">{d.slice(0, 3)}</th>)}
              <th className="px-2 text-center text-gold-light">Reg</th><th className="px-2 text-center text-amber-400">OT</th><th className="px-2 text-center">Total</th><th className="px-2 text-center">Res</th><th className="px-2 text-center">Status</th><th></th>
            </tr></thead>
            <tbody>{summary.rows.map((r) => (
              <tr key={r.driver_id} data-testid={`payroll-row-${r.driver_id}`} className="border-b border-line/60">
                <td className="py-2 pr-2 text-ivory">{r.name}</td>
                {r.days.map((d) => <td key={d.day} className="px-1 text-center text-muted-steel">{d.hours ? d.hours.toFixed(2) : "—"}</td>)}
                <td className="px-2 text-center text-gold-light">{r.regular_hours.toFixed(2)}</td>
                <td className="px-2 text-center text-amber-400">{r.overtime_hours.toFixed(2)}</td>
                <td className="px-2 text-center text-ivory">{r.total_hours.toFixed(2)}</td>
                <td className="px-2 text-center text-muted-steel">{r.reservations}</td>
                <td className="px-2 text-center" data-testid={`payroll-status-${r.driver_id}`}>
                  {r.status === "Approved"
                    ? <span className="rounded-full border border-gold/40 bg-gold/15 px-2 py-0.5 text-[10px] font-medium text-gold-light" title={`${r.approved_by || ""} · ${r.approved_at || ""} · v${r.version}`}>Approved v{r.version}</span>
                    : r.status === "Needs re-approval"
                      ? <span className="rounded-full border border-amber-700/40 bg-amber-900/20 px-2 py-0.5 text-[10px] text-amber-300" title={`Superseded by a newer approved correction · last v${r.version}`}>Needs re-approval</span>
                      : <span className="rounded-full border border-muted-steel/30 bg-obsidian-900 px-2 py-0.5 text-[10px] text-muted-steel">Pending</span>}
                </td>
                <td className="px-2 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    {canApprove && r.status !== "Approved" && (
                      <button data-testid={`payroll-approve-${r.driver_id}`} disabled={approving === r.driver_id} onClick={() => approve(r.driver_id)} className="inline-flex items-center gap-1 rounded-md border border-gold/40 bg-gold/10 px-2 py-1 text-[11px] font-medium text-gold-light hover:bg-gold/20 disabled:opacity-50">{approving === r.driver_id ? <Loader2 className="h-3 w-3 animate-spin" /> : null} {r.status === "Needs re-approval" ? "Re-approve" : "Approve"}</button>
                    )}
                    <button data-testid={`payroll-pdf-${r.driver_id}`} onClick={() => download(`${base}/report.pdf${pdfQS(`driver_id=${r.driver_id}`)}`, `payroll-${r.driver_id}.pdf`)} className="inline-flex items-center gap-1 rounded-md border border-gold/30 px-2 py-1 text-[11px] text-gold-light hover:bg-gold/10"><FileDown className="h-3 w-3" /> PDF</button>
                  </div>
                </td>
              </tr>))}</tbody>
          </table></div>
        )}
      </Panel>
    </div>
  );
}

export function DriverHours() {
  const [data, setData] = useState(null);
  const [week, setWeek] = useState(null);
  useEffect(() => {
    const qs = week ? `?week_start=${week.slice(0, 10)}` : "";
    api.get(`/driver/payroll/summary${qs}`).then((r) => { setData(r.data); if (!week) setWeek(r.data.week_start); }).catch(() => setData({}));
  }, [week]);
  const step = (d) => { const x = new Date(week); x.setDate(x.getDate() + d); setWeek(x.toISOString()); };
  if (!data) return <div className="py-8 text-center text-sm text-muted-steel">Loading hours…</div>;
  return (
    <div data-testid="driver-hours" className="space-y-3">
      <div className="flex items-center justify-between">
        <button onClick={() => step(-7)} className="rounded-md border border-gold/30 p-1.5 text-gold-light"><ChevronLeft className="h-4 w-4" /></button>
        <span className="text-xs text-ivory">{data.week_start ? fmtWeek(data.week_start, data.week_end) : ""}</span>
        <button onClick={() => step(7)} className="rounded-md border border-gold/30 p-1.5 text-gold-light"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {(data.days || []).map((d) => (<div key={d.day} className="rounded-lg border border-line bg-obsidian-900 p-2"><p className="text-[9px] uppercase text-muted-steel">{d.day.slice(0, 3)}</p><p className="mt-1 text-sm text-ivory">{d.hours ? d.hours.toFixed(1) : "—"}</p></div>))}
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg border border-gold/25 bg-obsidian-900 p-3"><p className="text-[9px] uppercase text-gold-dark">Regular</p><p className="mt-1 text-lg text-gold-light">{(data.regular_hours ?? 0).toFixed(2)}</p></div>
        <div className="rounded-lg border border-amber-700/30 bg-obsidian-900 p-3"><p className="text-[9px] uppercase text-amber-500">Overtime</p><p className="mt-1 text-lg text-amber-400">{(data.overtime_hours ?? 0).toFixed(2)}</p></div>
        <div className="rounded-lg border border-line bg-obsidian-900 p-3"><p className="text-[9px] uppercase text-muted-steel">Total</p><p className="mt-1 text-lg text-ivory">{(data.total_hours ?? 0).toFixed(2)}</p></div>
      </div>
      <p className="text-[10px] text-dim-charcoal">{data.reservations ?? 0} reservations · trip-clock hours only (preparation & other duties tracked separately).</p>
    </div>
  );
}
